import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { UserAgent } from '@mirinae/js-utils';
import { fontDefault, fontDefaultAlt } from '@mirinae/react-ui';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { lessonsPaths, explorerPaths, membersPaths, authPaths} from '@mirinae/shared/defines/paths';
import { keywordsByLetter } from '@mirinae/lessons/components/lesson/elements/shared/glossaryTerms';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import IconBook from '@mirinae/lessons/components/lesson/elements/icons/icon-book.svg';

import Localization from '@mirinae/shared/il8n';

const isMobile = !UserAgent.os.isOthers;

const hangul = require('hangul-js');

const classNamesForTextType = {
    text: 'text-element',
    goalTitle: 'goal-title-element',
    goalText: 'goal-text-element',
    annotation: 'annotation-text-element',
    bubbleTextUp: 'bubble-text-up',
    bubbleTextDown: 'bubble-text-down',
    directionsText: 'directions',
    callToActionText: 'call-to-action',
    goodJobText: 'good-job-text',
    congratsText: 'congrats-text',
    irregularTitle: 'irregular-title',
    completeGoalText: 'complete-goal-text',
    imageAndTextTitle: 'image-and-text-title',
};

const verticalAlign = { CENTER: 'center', TOP: 'flex-start', BOTTOM: 'flex-end' };
const horizontalAlign = { CENTER: 'center', LEFT: 'left', RIGHT: 'right' };
const textAlign = { CENTER: 'center', LEFT: 'left', RIGHT: 'right' };

//     bold: { style: 'Bold' },
//     italic: { style: 'Italic' },
//     larger: { fontSizeScale: 1.5 },
//     smaller: { fontSizeScale: 0.75 },
//     highlight: { style: 'Bold', color: [{ type: 'SOLID', color: colorMap.highlight }]},
//     title: { style: 'Bold', color: [{ type: 'SOLID', color: { r: 87 / 255, g: 122 / 255, b: 193 / 255 } }]},
//     noun: { color: [{ type: 'SOLID', color: colorMap.noun }] },
//     verb: { color: [{ type: 'SOLID', color: colorMap.verb }] },

// update the above and complete the following when styles finalized

const styles = {
    bold: 'font-weight: bold;',
    italic: 'font-style: italic;',
    highlight: 'font-weight: bold; color: #F87313;',
    pattern: 'font-weight: bold; color: #F87313;', // #F6C844;',
    goalPattern: 'font-weight: 800; color: #F6C844;',
    target: 'font-weight: 500; color: #3C3C3B;',
    verb: 'color: #C45F5B;',
    verbHighlight: 'color: #F6C844',
    miniLesson: 'color: #5D99D0',
    special: `font-family: ${fontDefaultAlt}; font-weight: bold; color: #F7A491;`,
    koreanBold: `font-family: ${fontDefault}; font-weight: 600; color: #000000;`, // #3C3C3B;`, // #F2A756;`, // #AF6932;', // #F2A756;',
};

const TextWrapper = styled.div`
    display: flex;
    ${props => props.textStyle && (`
        align-items: ${-verticalAlign[props.textStyle.textAlignVertical]};
        justify-content: ${horizontalAlign[props.textStyle.textAlignHorizontal]};
        text-align: ${textAlign[props.textStyle.textAlignHorizontal]};
        font-size: ${props.textStyle.fontSize}px;
        font-family: ${props.textStyle.fontName.family};
        font-style: ${props.textStyle.fontName.style};
        font-weight: ${props.textStyle.fontWeight};
        color: rgb(${parseInt(props.textStyle.color[0].color.r * 255, 10)}, ${parseInt(props.textStyle.color[0].color.g * 255, 10)}, ${parseInt(props.textStyle.color[0].color.b * 255, 10)}); // should handle style-runs??
    `)};
    max-width: calc(100vw - 60px);
`;

const TextSpan = styled.span`
    ${props => props.span.style && styles[props.span.style]};
    white-space: pre-wrap;
`;

const GlossarySpan = styled.span`
    color: ${lessonsPalette.default[6]}; 
    font-weight: 400;
    padding: 0;
    line-height: 150%;
    cursor: pointer;
    
    :hover {
        text-decoration: underline;
    }
`;

const LinkSpan = styled.span`
    color: ${lessonsPalette.default[6]}; 
    text-decoration: underline;
    cursor: pointer;
`;

export const textFromParts = (parts) => parts.map(p => (p.color === 'space' ? ' ' : (p.color.match(/separator|separate|merge/) ? '' : p.value))).join('');

export const mostlyHangul = (text) => {
    // predicate returns true if text contains mostly hangul characters
    let hangulCount = 0;
    let romanCount = 0;
    Array.from(text).forEach(c => {
        if (!c.match(/\s/)) {
            if (hangul.isHangul(c))
                hangulCount += 1;
            else if (c.match(/[a-zA-Z]/))
                romanCount += 1;
        }
    });
    // console.log('*-- ', hangulCount, romanCount, text)
    return hangulCount > 0.75 * romanCount;
};

export const FormattedInlineText = (props) => <FormattedText {...props} style={{ display: 'inline' }} />;

export const FormattedText = ({ styles, links, text, linkClicked, style }) => {
    const txt = text.replace(/\\n/g, '\n');
    const [spans, setSpans] = useState([]);
    const player = useContext(PlayerContext);
    
    useEffect(() => {
        // pre-preprocess text & link spans
        const stylePlusLinks = (styles || []).concat(links || []).sort((a, b) => a.start - b.start);
        let ti = 0;
        const spans = [];

        const formatPlainText = (textRun) => {
            // embolden any hangul characters in plain text
            let si = 0; let ei = 0;
            const isHangul = (c) => hangul.isHangul(c) || hangul.isJong(c) || hangul.isCho(c) || hangul.isVowel(c);
            const isPrefixedHangulAt = (i) => { // include '-' & digits prefixes in hangul style
                if (isHangul(textRun[i]) || (textRun[i] === '-' && isHangul(textRun[i + 1])))
                    return true;
                const digits = textRun.substring(i).match(/^\d+/);
                if (digits && isHangul(textRun[i + digits[0].length]))
                    return true;
            };
            while (si < textRun.length) {
                if (isPrefixedHangulAt(si)) {
                    while (ei < textRun.length && (isHangul(textRun[ei]) || textRun[ei] === ' ' || textRun[ei].match(/-|\d/))) ei += 1;
                    spans.push({ text: textRun.substring(si, ei), korean: true, style: 'koreanBold' });
                } else {
                    while (ei < textRun.length && !isPrefixedHangulAt(ei)) ei += 1;
                    spans.push({ text: textRun.substring(si, ei) });
                }
                si = ei;
            }
        };

        stylePlusLinks.forEach(sl => {
            formatPlainText(txt.substring(ti, sl.start));
            spans.push({ text: txt.substring(sl.start, sl.end), ...sl });
            ti = sl.end;
        });
        if (ti < txt.length)
            formatPlainText(txt.substring(ti, txt.length));

        const fspans = [];
        // check text spans for glossary keywords and insert glossary spans if so
        spans.forEach(sp => {
            if (sp.ref || sp.style)
                fspans.push(sp);
            else {
                const text = `${sp.text.toLowerCase()} `;
                let si = 0; let wi = 0; let ei = 0;
                while (wi < text.length) {
                    const kwds = keywordsByLetter[text[wi]];
                    if (kwds)
                        for (let i = 0; i < kwds.length; i += 1) {
                            if (text.length - wi > kwds[i].length && text.startsWith(kwds[i], wi)) {
                                if (wi > si)
                                    fspans.push({ text: sp.text.substring(si, wi) });
                                ei = wi + kwds[i].length;
                                while (ei < text.length && text[ei].match(/\w/)) ei += 1;
                                const phrase = sp.text.substring(wi, ei);
                                if (!player.glossaryPhrases.has(phrase)) {
                                    fspans.push({ text: phrase, glossaryKey: kwds[i] });
                                    player.glossaryPhrases.add(phrase);
                                } else {
                                    fspans.push({ text: phrase });
                                }
                                wi += kwds[i].length;
                                si = ei;
                                break;
                            }
                        }
                    while (wi < text.length && !text[wi].match(/\s/)) wi += 1;
                    while (wi < text.length && text[wi].match(/\s/)) wi += 1;
                }
                if (wi > si)
                    fspans.push({ text: sp.text.substring(si, wi) });
            }
        });
        //
        setSpans(fspans);
    }, []);

    const onLinkClick = (span) => {
        if (span.ref.startsWith('lesson://')) {
            window.open(span.ref.replace(/^lesson:\/\//, ''));
        } else {
            window.open(span.ref, '_blank');
        }
    };

    const onGlossaryClick = (span) => {
        const url = `${explorerPaths.glossary}?srch=${encodeURIComponent(span.glossaryKey)}`;
        GTM.userAction('glossary-word', { word: span.glossaryKey }, 'lessons');
        window.open(url, '_blank');
    };

    return (
        <div style={style}>
            { spans.map((span, i) => (span.ref ? (
                <LinkSpan key={i} span={span} onClick={() => onLinkClick(span)}>{span.text}</LinkSpan>
            ) : (span.glossaryKey ? (
                <GlossarySpan key={i} span={span} onClick={() => onGlossaryClick(span)} title="Click to open glossary definition for this phrase">{span.text}</GlossarySpan>
            ) : (
                <TextSpan key={i} span={span}>
                    {span.text}
                </TextSpan>
            ))))}
        </div>
    );
};

const ExplanationWrapper = styled.div`
    text-align: left;
`;
const ExplanationTitle = styled.div`
    margin-bottom: 10px;
    padding-left: 25px;
    font-weight: 700;
    background: url(${IconBook}) no-repeat 0 50%;
`;
export const FormattedTextField = ({ element, field, text, linkClicked }) => {

    const textStyle = { ...(isMobile && field.mobile ? field.mobile : field).textStyle } || {};
    // if (textStyle)  element.size now ignored, font-size comes from element.type className or template field.textStlye or element.fontSize override
    //     textStyle.fontScale = size ? (typeof size === 'string' ? Number(size.split('%')[0]) / 100.0 : size) : 1;
    if (element && element.fontSize && element.fontSize !== 'default') {
        textStyle.fontSize = element.fontSize.replace('px', '');
    }
    return (
        <TextWrapper textStyle={textStyle}>
            {element.title === 'explanation' ? (
                <ExplanationWrapper>
                    <ExplanationTitle>{Localization['player-definition']}</ExplanationTitle>
                    <FormattedText text={text || field.value} styles={field.styles} links={field.links} linkClicked={linkClicked} />
                </ExplanationWrapper>
            ) : (
                <FormattedText text={text || field.value} styles={field.styles} links={field.links} linkClicked={linkClicked} />  
            )}
        </TextWrapper>
    );
};

const Text = ({ element }) => {
    const player = useContext(PlayerContext);

    const linkClicked = (span) => {
        console.log(span);
        // handle click on a link span, target is in span.ref
    };

    return (
        <Element element={element} className={`${classNamesForTextType[element.type]} el-${element.componentName}`}>
            <FormattedTextField element={element} field={element.fieldsByName.text} text={player.fieldValue(element, 'text')} linkClicked={linkClicked} />
        </Element>
    );
};

export default Text;
